import { posthog } from 'posthog-js';
import { useSyncExternalStore } from 'react';
import { config } from '../config';
import { CONSENT_TEXT_VERSION, PRIVACY_POLICY_VERSION } from './versions';

export { CONSENT_TEXT_VERSION, PRIVACY_POLICY_VERSION };

/**
 * Analytics consent for the recruiter app.
 *
 * PostHog starts opted out (see `src/index.tsx`) and stores nothing in the browser until the
 * recruiter chooses. The choice is remembered by PostHog in the browser, so it is asked once
 * per browser. Feature flags work whatever the choice, because they only need the company.
 */
export type ConsentStatus = 'granted' | 'denied' | 'pending';

export type AnalyticsUser = {
  id: string;
  clientId?: string | null;
  /** Sent to feature flags for Hubert staff only (see `flagProperties`). */
  email?: string | null;
  isAdmin: boolean;
  isInternal: boolean;
};

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

let currentUser: AnalyticsUser | null = null;
let suspended = false;

export const getConsentStatus = (): ConsentStatus => {
  try {
    return posthog.get_explicit_consent_status();
  } catch {
    return 'pending';
  }
};

/** Re-renders a component when the consent choice changes. */
export const useConsentStatus = (): ConsentStatus =>
  useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getConsentStatus,
    () => 'pending',
  );

const startIdentifiedTracking = (user: AnalyticsUser) => {
  // A random internal ID and non-personal flags only. Never name or e-mail.
  posthog.identify(user.id, {
    // Same ID as the distinct ID, saved as a property so flags can target single users by it.
    userId: user.id,
    clientId: user.clientId ?? undefined,
    isAdmin: user.isAdmin,
    isInternal: user.isInternal,
  });
  posthog.register({ app_version: config.VITE_VERSION });
  posthog.startSessionRecording();
};

/**
 * What PostHog's feature flags match on. Sent with every flag check, also without consent and
 * under "Login as" (when nothing is identified), so flags show what the customer would see.
 * Customers are targeted by company (`clientId`) or, for single people, by user ID; only Hubert
 * staff are targeted by e-mail, so a customer's e-mail never goes to PostHog.
 */
const flagProperties = (user: AnalyticsUser): Record<string, string> => ({
  userId: user.id,
  ...(user.clientId ? { clientId: user.clientId } : {}),
  ...(user.isInternal && user.email ? { email: user.email } : {}),
});

/**
 * Called when the logged-in user is known. Always gives feature flags the company and
 * `flagProperties`; nothing is captured unless the user has allowed analytics.
 */
export const setAnalyticsUser = (user: AnalyticsUser, groupName?: string | null) => {
  currentUser = user;
  if (user.clientId) posthog.group('tenant', user.clientId, groupName ? { name: groupName } : undefined);
  // posthog-js merges flag properties, so clear the last user's first (e.g. a staff e-mail).
  posthog.resetPersonPropertiesForFlags(false);
  posthog.setPersonPropertiesForFlags(flagProperties(user));
  if (!suspended && getConsentStatus() === 'granted') startIdentifiedTracking(user);
};

export type ConsentSource = 'privacy_window' | 'profile';

/**
 * Records consent and starts tracking. PostHog's opt-in event is the proof of consent (GDPR art.
 * 7(1)): who, when, where they agreed, and which consent text and privacy policy they saw.
 */
export const grantConsent = (source: ConsentSource = 'privacy_window') => {
  posthog.opt_in_capturing({
    captureProperties: {
      consent_source: source,
      consent_text_version: CONSENT_TEXT_VERSION,
      privacy_policy_version: PRIVACY_POLICY_VERSION,
    },
  });
  if (currentUser && !suspended) startIdentifiedTracking(currentUser);
  notify();
};

export const denyConsent = () => {
  posthog.stopSessionRecording();
  posthog.opt_out_capturing();
  notify();
};

/**
 * Stops all capturing for this page load without changing the stored choice. Used under "Login as"
 * (impersonatedBy in the login token), so Hubert staff acting as a customer are never counted as that customer.
 * Events are dropped by `dropWhileSuspended`, which is registered as `before_send` in
 * `src/index.tsx`.
 */
export const suspendAnalytics = () => {
  suspended = true;
  try {
    posthog.stopSessionRecording();
  } catch {
    // ignore
  }
};

/** `before_send` hook: drops every event while analytics is suspended. */
export const dropWhileSuspended = <T>(event: T): T | null => (suspended ? null : event);

/**
 * Logs the analytics identity out while keeping the recruiter's consent choice.
 * `posthog.reset()` also clears the stored choice, so it is re-applied afterwards; otherwise
 * recruiters would be asked again after every logout.
 */
export const resetAnalyticsKeepingConsent = () => {
  const status = getConsentStatus();
  currentUser = null;
  try {
    posthog.reset();
    if (status === 'granted' && !suspended) posthog.opt_in_capturing({ captureEventName: false });
    if (status === 'denied') posthog.opt_out_capturing();
  } catch {
    // ignore
  }
  notify();
};
